/**
 * Pre-chargement des sous-RG avant un calcul qui a besoin du graphe complet
 * (ex: moteur de prime). Le comparateur lazy-load les sous-RG au clic — avant
 * de taper le moteur, on s'assure que tous les stubs sont hydrates.
 *
 * Strategie: on walk recursivement `rg.responses[].responseGroups`, on collecte
 * les uuids non hydrates, on les pousse au rgBatchLoader (debounce 200ms + flush
 * auto a 10 uuids), puis on attend les promesses. Plusieurs passes sont
 * necessaires car un sous-RG fraichement charge peut lui-meme contenir d'autres
 * stubs imbriques.
 */

import type ResponseGroup from "@/entity/ResponseGroup";
import type Response from "@/entity/Response";
import { rgBatchLoader } from "@/services/RgBatchLoader";
import store from "@/store";

function collectPendingUuids(rg: any, acc: Set<string>): void {
  const responses: any[] = Array.isArray(rg?.responses) ? rg.responses : [];
  for (const res of responses) {
    const subs: any[] = Array.isArray(res?.responseGroups) ? res.responseGroups : [];
    for (const sub of subs) {
      if (!sub || !sub.uuid) continue;
      const hydrated = Array.isArray(sub.responses) && sub.responses.length > 0;
      if (hydrated) {
        collectPendingUuids(sub, acc);
      } else {
        // Pas d'entree store ou entree vide — a charger.
        const stored = store.state.formStore?.responseGroups?.[sub.uuid];
        const storedResponses = store.state.formStore?.responses?.[sub.uuid];
        const storeHydrated =
          stored && storedResponses && Object.keys(storedResponses).length > 0;
        if (storeHydrated) {
          continue;
        }
        acc.add(sub.uuid);
      }
    }
  }
}

/**
 * S'assure que tous les sous-RG accessibles depuis `rg` sont charges.
 * Resout quand plus aucun stub a hydrater (ou apres `maxPasses` iterations).
 */
export async function ensureSubRgsLoaded(
  rg: ResponseGroup | any,
  maxPasses = 5
): Promise<void> {
  if (!rg) return;
  for (let pass = 0; pass < maxPasses; pass++) {
    const pending = new Set<string>();
    collectPendingUuids(rg, pending);
    if (pending.size === 0) return;
    await Promise.all(Array.from(pending).map((u) => rgBatchLoader.loadRg(u)));
  }
}
